// TEMPORARY dev-only mock for the s5-b10-* mixes.
//
// Only the SLEEVE WEIGHTS and the sleeve identity strings below are invented.
// Every other value - fund name, symbol, url, holdings, price to book, expense
// ratio, weighted duration/maturity, every classification, and the per-fund
// benchmark - is pulled live from data2.ifa.com at request time, so the mockup
// tracks the real data.
//
// Weights are sleeve-relative and scaled by the portfolio number, so a single
// definition covers s5-b10-0 (all fixed income) through s5-b10-100 (all equity).

export const MOCK_S5_B10_SLEEVE_WEIGHTS: Record<string, { investment_id: number; percent: number }[]> = {
    // 60/40 within the equity sleeve
    equity: [
        { investment_id: 31765, percent: 60 },   // VTI
        { investment_id: 31766, percent: 40 }    // VXUS
    ],
    // 50/30/12/8 within the fixed income sleeve
    fixedincome: [
        { investment_id: 13758, percent: 50 },   // BND
        { investment_id: 31767, percent: 30 },   // VFIDX
        { investment_id: 31768, percent: 12 },   // VFSUX
        { investment_id: 31769, percent: 8 }     // VWETX
    ]
};

// Sleeve identity for the new lineup. Values are taken from the vocabulary the live
// implementations/equity and implementations/fixedincome endpoints already use.
// Equity is VTI + VXUS (both large blend, both ETFs); fixed income is BND (ETF) plus
// three Investment-Grade Admiral mutual funds spanning broad/intermediate/short/long.
export const MOCK_S5_B10_SLEEVES: Record<string, { strategy: string; fund_type_name: string }> = {
    equity: { strategy: 'Large/Blend', fund_type_name: 'ETFs' },
    fixedincome: { strategy: 'Broad/Intermediate/Short', fund_type_name: 'ETFs & Mutual Funds' }
};

const SLEEVE_ASSET_CLASS: Record<string, number> = { equity: 1, fixedincome: 2 };

export const MOCK_S5_B10_IDS = [
    ...MOCK_S5_B10_SLEEVE_WEIGHTS['equity'],
    ...MOCK_S5_B10_SLEEVE_WEIGHTS['fixedincome']
].map(row => row.investment_id);

const round2 = (value: number) => Math.round(value * 100) / 100;

// The live API keeps every fund in the allocation and zeroes the idle sleeve rather
// than dropping rows (verified against s5-b10-0 and s5-b10-100), so the mock does too.
export function mockWeightsForPortfolio(portfolioNumber: number) {
    const share: Record<string, number> = {
        equity: portfolioNumber / 100,
        fixedincome: (100 - portfolioNumber) / 100
    };
    return ['equity', 'fixedincome']
        .flatMap(sleeve => MOCK_S5_B10_SLEEVE_WEIGHTS[sleeve].map(weight => ({
            investment_id: weight.investment_id,
            percent: round2(weight.percent * share[sleeve]),
            asset_class: SLEEVE_ASSET_CLASS[sleeve]
        })))
        .map((row, order) => ({ ...row, order }));
}

const inList = (ids: number[]) => `(${ids.join(',')})`;

export const mockDetailsUrl = (ids: number[]) =>
    `https://data2.ifa.com/odata/v1/investment/details-latest?investment_ids=${ids.join(',')}`;

export const mockInvestmentsUrl = (ids: number[]) =>
    `https://data2.ifa.com/odata/v1/investment?$filter=id%20in%20${inList(ids)}&$select=id,symbol,name,url`;

export const mockDurationsUrl = (ids: number[]) =>
    `https://data2.ifa.com/odata/v1/duration/latest?$filter=investment_id%20in%20${inList(ids)}`;

export const mockMaturitiesUrl = (ids: number[]) =>
    `https://data2.ifa.com/odata/v1/maturity/latest?$filter=investment_id%20in%20${inList(ids)}`;

// Per-fund benchmark choices live in IFA_Investments_Benchmarks, so the mock reads
// them rather than hard-coding them - re-pointing a benchmark in the admin shows up
// here on the next reload.
export const MOCK_S5_B10_BENCHMARKS_URL =
    `https://data2.ifa.com/odata/v1/investments-benchmarks?$filter=investment_id%20in%20${inList(MOCK_S5_B10_IDS)}`;

export interface MockRefData {
    details: any[];
    investments: any[];
    durations: any[];
    maturities: any[];
}

const CLASSIFICATION_MAPS: Record<string, string> = {
    screen: 'screen_map',
    type: 'fund_type_map',
    asset_class: 'asset_class_map',
    region: 'region_map',
    manager: 'manager_map',
    size: 'size_map',
    style: 'style_map',
    maturity: 'maturity_map',
    security_type: 'security_type_map'
};

function addClassificationText(row: any, classifications: any): any {
    for (const [field, mapName] of Object.entries(CLASSIFICATION_MAPS)) {
        row[`${field}_text`] = classifications[mapName]
            .find((item: any) => item.id === row[field])?.value ?? null;
    }
    return row;
}

// Weighted duration/maturity are keyed off whichever investment the row ends up
// representing, matching how ImplementationsController joins them onto holdings.
function addWeighted(row: any, ref: MockRefData): any {
    const duration = ref.durations.find(x => x.investment_id === row.investment_id)?.duration;
    const maturity = ref.maturities.find(x => x.investment_id === row.investment_id)?.maturity;
    row.duration_weighted = duration == null ? null : round2(Number(duration));
    row.maturity_weighted = maturity == null ? null : round2(Number(maturity));
    return row;
}

function buildGroupTotals(sleeve: any, rows: any[]): any {
    return Object.fromEntries(
        Object.keys(sleeve.percent_total_grouped_by).map(field => {
            const totals = new Map<number, number>();
            for (const row of rows) {
                const id = row[field === 'fund_type' ? 'type' : field];
                if (id != null) {
                    totals.set(id, (totals.get(id) ?? 0) + row.percent);
                }
            }
            return [field, Array.from(totals, ([id, sum]) => ({ id, percent_sum: round2(sum) }))];
        })
    );
}

function buildSleeve(sleeve: any, rows: any[], identity?: { strategy: string; fund_type_name: string }): any {
    return {
        ...sleeve,
        ...(identity ?? {}),
        percent: round2(rows.reduce((sum, row) => sum + row.percent, 0)),
        percent_total_grouped_by: buildGroupTotals(sleeve, rows)
    };
}

export function buildMockAllocationS5B10(base: any, ref: MockRefData, portfolioNumber: number): any {
    const allocation = mockWeightsForPortfolio(portfolioNumber).map(weight => {
        const detail = ref.details.find(row => row.investment_id === weight.investment_id);
        const investment = ref.investments.find(row => row.id === weight.investment_id);
        if (!detail || !investment || !investment.name || !investment.symbol
            || detail.asset_class !== weight.asset_class) {
            throw new Error(`Missing or invalid API fund data for ${weight.investment_id}`);
        }
        const row = {
            ...detail,
            ...weight,
            name: investment.name,
            symbol: investment.symbol,
            url: investment.url ?? null,
            price_to_book: detail.price_to_book ?? detail.weighted_book_to_market ?? null,
            // MOCK: the fund name and the holdings count both link to the investment's
            // url. The live allocation endpoint drives those two links off factsheet and
            // holdingsheet, which are empty for these funds, so point them at url here
            // instead of touching the shared allocation-table templates.
            factsheet: investment.url ?? null,
            holdingsheet: investment.url ?? null
        };
        return addWeighted(addClassificationText(row, base.classifications), ref);
    });
    return {
        ...base,
        allocation,
        equity: buildSleeve(base.equity, allocation.filter(row => row.asset_class === 1),
            MOCK_S5_B10_SLEEVES['equity']),
        fixedincome: buildSleeve(base.fixedincome, allocation.filter(row => row.asset_class === 2),
            MOCK_S5_B10_SLEEVES['fixedincome'])
    };
}

// Identity + classification come from the benchmark; everything quantitative stays on
// the fund's row. That mirrors the live benchmark-allocation response, which reuses the
// fund's percent, holdings, market cap, price to book and sheet links verbatim.
const BENCHMARK_OVERLAY_FIELDS = [
    'screen', 'type', 'asset_class', 'region', 'manager', 'size', 'style', 'maturity', 'security_type'
];

export function buildMockBenchmarkAllocationS5B10(
    base: any,
    mocked: any,
    benchmarkTypeId: number,
    mappings: any[],
    ref: MockRefData
): any {
    const missing_benchmarks: number[] = [];
    const allocation = mocked.allocation.map((fundRow: any) => {
        const benchmarkId = mappings
            .find(m => m.investment_id === fundRow.investment_id && m.benchmark_type_id === benchmarkTypeId)
            ?.benchmark_id;
        const detail = benchmarkId == null ? null : ref.details.find(row => row.investment_id === benchmarkId);
        const investment = benchmarkId == null ? null : ref.investments.find(row => row.id === benchmarkId);
        if (!detail || !investment) {
            // Same fallback the API uses: hand back the fund row and report it as missing.
            missing_benchmarks.push(fundRow.investment_id);
            return { ...fundRow };
        }
        const row: any = { ...fundRow, investment_id: benchmarkId, name: investment.name, symbol: investment.symbol };
        for (const field of BENCHMARK_OVERLAY_FIELDS) {
            row[field] = detail[field];
        }
        return addWeighted(addClassificationText(row, base.classifications), ref);
    });
    const identity = (sleeveKey: string, rows: any[]) =>
        rows.every((row: any) => missing_benchmarks.includes(row.investment_id))
            ? undefined
            : { ...MOCK_S5_B10_SLEEVES[sleeveKey], fund_type_name: base[sleeveKey].fund_type_name };
    const equityRows = allocation.filter((row: any) => row.asset_class === 1);
    const fixedRows = allocation.filter((row: any) => row.asset_class === 2);
    return {
        ...base,
        allocation,
        missing_benchmarks,
        equity: buildSleeve(base.equity, equityRows, identity('equity', equityRows)),
        fixedincome: buildSleeve(base.fixedincome, fixedRows, identity('fixedincome', fixedRows))
    };
}

// The mix page header reads strategy / fund_type_name from the implementation choice
// lists, not from the allocation response, so patch the s5 and b10 entries to match.
export function patchMockImplementationChoice(items: any[], id: number, sleeveKey: string): any[] {
    if (!Array.isArray(items)) { return items; }
    return items.map(item => item?.id === id ? { ...item, ...MOCK_S5_B10_SLEEVES[sleeveKey] } : item);
}
